import customerRideApi from 'api/customerRideApi';
import React, { useCallback, useEffect, useState } from 'react';
import QRCode from 'react-qr-code';
import { formatRideDateTime, formatRideMoney, rideErrorMessage } from '../rideUtils';
import RideAppIcon from './RideAppIcon';
import RidePortal from './RidePortal';
import { RideScheduleSheet, RideSeatSheet } from './RideSearchOptionSheets';

const getRideWallet = (response) => {
  const profile = response?.profile || response?.user || response;
  const wallets = Array.isArray(profile?.rideWallets) ? profile.rideWallets : [];
  return wallets[0] || null;
};

const formatPublicPromoBenefit = (promo) => {
  const discountType = String(promo?.discountType || '').toUpperCase();
  if (discountType === 'PERCENT') {
    const percent = Number(promo.discountPercent || 0);
    const max = promo.maxDiscountAmount != null
      ? `, tối đa ${formatRideMoney(promo.maxDiscountAmount, 'VND')}`
      : '';
    return `Giảm ${percent}%${max}`;
  }
  if (discountType === 'FIXED') return `Giảm ${formatRideMoney(promo.discountAmount, 'VND')}`;
  if (discountType === 'TARGET_FARE') return `Giá còn ${formatRideMoney(promo.targetFare, 'VND')}`;
  return promo.description || 'Ưu đãi chuyến xe';
};

const formatPublicPromoCondition = (promo) => {
  const conditions = [];
  const minimum = Number(promo?.minTotalFare || 0);
  if (minimum > 0) conditions.push(`Đơn tối thiểu ${formatRideMoney(minimum, 'VND')}`);
  if (promo?.endAt) {
    const endAt = new Date(promo.endAt);
    if (!Number.isNaN(endAt.getTime())) conditions.push(`Hạn dùng ${endAt.toLocaleDateString('vi-VN')}`);
  }
  return conditions.length ? conditions.join(' · ') : 'Áp dụng cho chuyến hợp lệ';
};

const RideScheduledDemandModal = ({ initial, onClose, onCreated }) => {
  const [departureAt, setDepartureAt] = useState(initial.departureAt || null);
  const [seats, setSeats] = useState(Number(initial.seats) || 1);
  const [paymentMethod, setPaymentMethod] = useState('BIDV');
  const [note, setNote] = useState('');
  const [optionSheet, setOptionSheet] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [fareState, setFareState] = useState('loading');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromoCode, setAppliedPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const [publicPromos, setPublicPromos] = useState([]);
  const [promoListState, setPromoListState] = useState('loading');
  const [wallet, setWallet] = useState(null);
  const [walletState, setWalletState] = useState('loading');
  const [walletError, setWalletError] = useState('');

  const loadRideWallet = useCallback(async () => {
    setWalletState('loading');
    setWalletError('');
    try {
      const response = await customerRideApi.getProfile();
      setWallet(getRideWallet(response));
      setWalletState('success');
    } catch (requestError) {
      setWalletState('error');
      setWalletError(rideErrorMessage(requestError, 'Không thể lấy số dư Ví Xe ghép.'));
    }
  }, []);

  useEffect(() => {
    loadRideWallet();
    let active = true;
    Promise.resolve(customerRideApi.getPublicPromoCodes())
      .then((response) => {
        if (!active) return;
        const promos = Array.isArray(response?.promoCodes) ? response.promoCodes : [];
        // The API already hides account-exhausted codes. Keep this defensive
        // filter so an older/cached response can never advertise one as usable.
        setPublicPromos(promos.filter((promo) => promo?.available !== false));
        setPromoListState('success');
      })
      .catch(() => {
        if (active) setPromoListState('error');
      });
    return () => { active = false; };
  }, [loadRideWallet]);

  const refreshBidvDemand = useCallback(async () => {
    if (!created?.demandId || created.status !== 'AWAITING_PAYMENT') return;
    try {
      const response = await customerRideApi.getMyScheduledDemands();
      const updated = (response?.demands || []).find((item) => item.demandId === created.demandId);
      if (updated) setCreated(updated);
    } catch (requestError) {
      // Keep the QR visible while the payment status is being reconciled.
      // The next poll or the explicit refresh button can retry safely.
      setError(rideErrorMessage(requestError, 'Chưa kiểm tra được trạng thái thanh toán.'));
    }
  }, [created]);

  useEffect(() => {
    if (!created?.demandId || created.status !== 'AWAITING_PAYMENT') return undefined;
    const timer = window.setInterval(refreshBidvDemand, 5000);
    return () => window.clearInterval(timer);
  }, [created?.demandId, created?.status, refreshBidvDemand]);

  useEffect(() => {
    let active = true;
    const fetchEstimate = async () => {
      setFareState('loading');
      setError('');
      try {
        const response = await customerRideApi.previewScheduledDemandFare({
          pickup: initial.pickup,
          dropoff: initial.dropoff,
          seats,
          ...(appliedPromoCode ? { promoCode: appliedPromoCode } : {}),
          isCharter: false,
        });
        if (active) {
          setEstimate(response?.estimate || null);
          setFareState('success');
        }
      } catch (requestError) {
        if (!active) return;
        if (appliedPromoCode) {
          setPromoError(rideErrorMessage(requestError, 'Mã khuyến mại không áp dụng được cho chuyến này.'));
          setAppliedPromoCode('');
          return;
        }
        setFareState('error');
        setError(rideErrorMessage(requestError, 'Không thể tính giá dự kiến.'));
      }
    };
    fetchEstimate();
    return () => { active = false; };
  }, [appliedPromoCode, initial.dropoff, initial.pickup, seats]);

  const departure = departureAt ? new Date(departureAt) : null;
  const hasValidTime = departure && departure.getTime() > Date.now();
  const discount = Math.max(0, Number(estimate?.promoDiscountAmount || 0));
  const totalBeforeDiscount = Number(estimate?.totalFareBeforeDiscount ?? estimate?.totalFare ?? 0);
  const totalPayable = Math.max(0, totalBeforeDiscount - discount);
  const walletBalance = Number(wallet?.balance || 0);
  const walletShortfall = paymentMethod === 'WALLET'
    && walletState === 'success'
    && walletBalance < totalPayable;
  const walletUnavailable = paymentMethod === 'WALLET'
    && walletState !== 'success';
  const disabledReason = !hasValidTime
    ? 'Vui lòng chọn thời điểm đón.'
    : fareState === 'loading'
      ? 'Đang tính giá chuyến...'
      : fareState === 'error'
        ? error
        : walletUnavailable
          ? (walletState === 'loading' ? 'Đang kiểm tra số dư Ví Xe ghép...' : (walletError || 'Không thể lấy số dư Ví Xe ghép.'))
          : walletShortfall
          ? 'Số dư Ví Xe ghép chưa đủ. Hãy nạp thêm tiền để tiếp tục.'
          : '';
  const canSubmit = Boolean(hasValidTime && fareState === 'success' && estimate && !submitting && !walletShortfall && !walletUnavailable);
  const applyPromo = () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) {
      setPromoError('Nhập mã khuyến mại trước khi áp dụng.');
      return;
    }
    setPromoError('');
    setAppliedPromoCode(code);
  };

  const removePromo = () => {
    setPromoInput('');
    setPromoError('');
    setAppliedPromoCode('');
  };

  const selectPublicPromo = (code) => {
    setPromoInput(code);
    setPromoError('');
    setAppliedPromoCode(code);
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const departureFrom = new Date(departureAt);
      const response = await customerRideApi.createScheduledDemand({
        pickup: initial.pickup,
        dropoff: initial.dropoff,
        seats,
        departureFrom: departureFrom.toISOString(),
        departureTo: new Date(departureFrom.getTime() + 2 * 60 * 60 * 1000).toISOString(),
        paymentMethod,
        ...(appliedPromoCode ? { promoCode: appliedPromoCode } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        isCharter: false,
      });
      setCreated(response.demand || {});
    } catch (requestError) {
      setError(rideErrorMessage(requestError, 'Không thể gửi yêu cầu chuyến đi.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (created?.paymentMethod === 'BIDV' && created.status === 'AWAITING_PAYMENT') {
    const bidvPayment = created.bidvPayment || {};
    const paymentAmount = Math.max(0, Number(created.fare?.totalFare || totalPayable || 0) - Number(created.fare?.promoDiscountAmount || 0));
    return (
      <RidePortal>
        <div className="customer-ride-modal-backdrop" role="presentation">
          <section className="customer-ride-modal ride-scheduled-modal ride-bidv-payment-modal" role="dialog" aria-modal="true" aria-label="Thanh toán chuyển khoản BIDV">
            <button className="customer-ride-modal-close" type="button" aria-label="Đóng" onClick={onClose}><RideAppIcon name="close" /></button>
            <div className="customer-ride-modal-heading"><span className="customer-ride-modal-icon"><RideAppIcon name="bidv" /></span><div><h2>Chuyển khoản BIDV</h2><p>Quét mã QR trong ứng dụng ngân hàng để thanh toán chuyến.</p></div></div>
            <div className="customer-ride-quote-route"><div><RideAppIcon name="pickup" /><span>{created.pickup?.address || initial.pickup.address}</span></div><div><RideAppIcon name="destination" /><span>{created.dropoff?.address || initial.dropoff.address}</span></div></div>
            <section className="ride-bidv-payment-card">
              <div className="ride-bidv-amount"><span>Tổng thanh toán</span><strong>{formatRideMoney(paymentAmount, created.fare?.currency || estimate?.currency || 'VND')}</strong></div>
              {bidvPayment.qrCode ? <QRCode value={String(bidvPayment.qrCode)} size={190} /> : <div className="ride-bidv-qr-placeholder"><RideAppIcon name="warning" /> Chưa tạo được mã QR BIDV</div>}
              <div className="ride-bidv-bank-info">
                <strong>Ngân hàng BIDV</strong>
                {bidvPayment.accountNumber && <p><span>Số tài khoản</span><b>{bidvPayment.accountNumber}</b></p>}
                {bidvPayment.accountName && <p><span>Chủ tài khoản</span><b>{bidvPayment.accountName}</b></p>}
                <p><span>Nội dung chuyển khoản</span><b>{created.demandId}</b></p>
              </div>
            </section>
            {error && <div className="customer-ride-alert danger">{error}</div>}
            <p className="ride-bidv-payment-hint"><RideAppIcon name="time" className="ride-icon-spin" /> Đang chờ BIDV xác nhận. Trang sẽ tự kiểm tra trạng thái.</p>
            <button type="button" className="customer-ride-secondary full" onClick={refreshBidvDemand}>Tôi đã chuyển khoản · Kiểm tra lại</button>
            <button type="button" className="customer-ride-primary full" onClick={onClose}>Để thanh toán sau</button>
          </section>
        </div>
      </RidePortal>
    );
  }

  if (created?.paymentMethod === 'BIDV' && created.status === 'PAYMENT_EXPIRED') {
    return (
      <RidePortal>
        <div className="customer-ride-modal-backdrop" role="presentation">
          <section className="customer-ride-modal ride-scheduled-modal ride-scheduled-modal--success" role="dialog" aria-modal="true" aria-label="Thanh toán BIDV đã hết hạn">
            <div className="ride-scheduled-success-icon"><RideAppIcon name="warning" /></div>
            <h2>Thanh toán đã hết hạn</h2>
            <p>Yêu cầu chưa được gửi tới tài xế. Hãy chọn phương thức khác hoặc tạo lại mã QR.</p>
            <button type="button" className="customer-ride-primary full" onClick={() => { setCreated(null); setPaymentMethod('BIDV'); setError(''); }}>Chọn lại phương thức thanh toán</button>
            <button type="button" className="customer-ride-secondary full" onClick={onClose}>Đóng</button>
          </section>
        </div>
      </RidePortal>
    );
  }

  if (created) {
    return (
      <RidePortal>
        <div className="customer-ride-modal-backdrop" role="presentation">
          <section className="customer-ride-modal ride-scheduled-modal ride-scheduled-modal--success" role="dialog" aria-modal="true" aria-label="Đã gửi yêu cầu chuyến đi">
            <div className="ride-scheduled-success-icon"><RideAppIcon name="check" /></div>
            <h2>Yêu cầu chuyến đi của bạn đã được gửi</h2>
            <p>Tài xế phù hợp sẽ thấy yêu cầu này trên bảng tìm khách.</p>
            <div className="ride-scheduled-summary"><span>{seats} ghế</span><span>{formatRideDateTime(departureAt)}</span>{created.demandId && <span>Mã yêu cầu: {created.demandId}</span>}</div>
            <button type="button" className="customer-ride-primary full" onClick={() => onCreated(created)}>Xong</button>
          </section>
        </div>
      </RidePortal>
    );
  }

  return (
    <RidePortal>
      <div className="customer-ride-modal-backdrop" role="presentation">
        <section className="customer-ride-modal ride-scheduled-modal" role="dialog" aria-modal="true" aria-label="Đặt chuyến hẹn giờ">
          <button className="customer-ride-modal-close" type="button" aria-label="Đóng" onClick={onClose}><RideAppIcon name="close" /></button>
          <div className="customer-ride-modal-heading"><span className="customer-ride-modal-icon"><RideAppIcon name="calendar" /></span><div><h2>Đặt chuyến hẹn giờ</h2><p>Gửi yêu cầu để tài xế phù hợp nhận chuyến.</p></div></div>
          <div className="customer-ride-quote-route"><div><RideAppIcon name="pickup" /><span>{initial.pickup.address}</span></div><div><RideAppIcon name="destination" /><span>{initial.dropoff.address}</span></div></div>
          <div className="ride-scheduled-options">
            <button type="button" onClick={() => setOptionSheet('SCHEDULE')}><span><RideAppIcon name="calendar" /> Thời điểm đón</span><b>{departureAt ? formatRideDateTime(departureAt) : 'Chọn thời điểm'}</b><RideAppIcon name="chevronRight" /></button>
            <button type="button" onClick={() => setOptionSheet('SEATS')}><span><RideAppIcon name="people" /> Số ghế</span><b>{seats} ghế</b><RideAppIcon name="chevronRight" /></button>
          </div>
          <section className="ride-scheduled-fare"><div className="ride-scheduled-fare-heading"><h3>Giá dự kiến</h3>{fareState === 'success' && <span>{seats} ghế</span>}</div>{fareState === 'loading' && <p><RideAppIcon name="time" /> Đang tính giá...</p>}{fareState === 'success' && estimate && <><div className="ride-scheduled-fare-row"><span>{formatRideMoney(estimate.farePerSeat, estimate.currency)} / ghế</span><b>{formatRideMoney(totalBeforeDiscount, estimate.currency)}</b></div>{discount > 0 && <div className="ride-scheduled-fare-row discount"><span>Giảm giá {estimate.promoCode || appliedPromoCode}</span><b>-{formatRideMoney(discount, estimate.currency)}</b></div>}<div className="ride-scheduled-fare-total"><span>Tổng thanh toán</span><strong>{formatRideMoney(totalPayable, estimate.currency)}</strong></div></>}{fareState === 'error' && <p className="danger-text">{error}</p>}</section>
          <section className="ride-scheduled-promo"><div className="ride-scheduled-section-title"><h3><RideAppIcon name="promo" /> Mã khuyến mại</h3>{appliedPromoCode && <button type="button" onClick={removePromo}>Bỏ mã</button>}</div><div className="customer-ride-promo"><input value={promoInput} onChange={(event) => { setPromoInput(event.target.value); setPromoError(''); }} placeholder="Nhập mã khuyến mại" aria-label="Mã khuyến mại" /><button type="button" onClick={applyPromo} disabled={fareState === 'loading'}>{appliedPromoCode ? 'Đã áp dụng' : 'Áp dụng'}</button></div>{publicPromos.length > 0 && <div className="ride-public-promos"><span>Mã khuyến mại khả dụng</span><div className="ride-public-promos__list">{publicPromos.map((promo) => <article key={promo.code}><button type="button" className={appliedPromoCode === promo.code ? 'selected' : ''} onClick={() => selectPublicPromo(promo.code)}><RideAppIcon name="promo" /><span><strong>{promo.code}</strong><small>{formatPublicPromoBenefit(promo)}</small><em>{formatPublicPromoCondition(promo)}</em></span><RideAppIcon name={appliedPromoCode === promo.code ? 'check' : 'add'} /></button></article>)}</div></div>}{promoListState === 'loading' && <p className="ride-promo-loading"><RideAppIcon name="time" className="ride-icon-spin" /> Đang tải mã công khai...</p>}{appliedPromoCode && !promoError && <p className="success-text"><RideAppIcon name="check" /> Đang áp dụng mã {appliedPromoCode}</p>}{promoError && <p className="danger-text">{promoError}</p>}</section>
          <section className="customer-ride-payment ride-scheduled-payment">
            <h3>Phương thức thanh toán</h3>
            <label className={paymentMethod === 'BIDV' ? 'selected' : ''}>
              <input type="radio" name="scheduled-payment" aria-label="Chuyển khoản BIDV" disabled={submitting} checked={paymentMethod === 'BIDV'} onChange={() => setPaymentMethod('BIDV')} />
              <RideAppIcon name="bidv" className="ride-payment-icon" />
              <span><b>Chuyển khoản BIDV</b><small>Quét QR BIDV, yêu cầu chỉ gửi tài xế sau khi thanh toán</small></span>
            </label>
            <label className={paymentMethod === 'CASH' ? 'selected' : ''}>
              <input type="radio" name="scheduled-payment" aria-label="Tiền mặt" disabled={submitting} checked={paymentMethod === 'CASH'} onChange={() => setPaymentMethod('CASH')} />
              <RideAppIcon name="cash" className="ride-payment-icon" />
              <span><b>Tiền mặt</b><small>Thanh toán trực tiếp cho tài xế</small></span>
            </label>
            <label className={paymentMethod === 'WALLET' ? 'selected' : ''}>
              <input type="radio" name="scheduled-payment" aria-label="Ví Xe ghép" disabled={submitting} checked={paymentMethod === 'WALLET'} onChange={() => setPaymentMethod('WALLET')} />
              <RideAppIcon name="rideWallet" className="ride-payment-icon" />
              <span><b>Ví Xe ghép</b><small>Thanh toán bằng số dư Ví Xe ghép</small></span>
            </label>
          </section>
          <label className="ride-scheduled-note">Ghi chú cho tài xế<textarea value={note} maxLength={500} onChange={(event) => setNote(event.target.value)} placeholder="Ví dụ: Đón ở sảnh A, gọi trước khi đến" /></label>
          {error && fareState !== 'error' && <div className="customer-ride-alert danger">{error}</div>}
          <div className="ride-scheduled-footer"><small>{canSubmit ? `${seats} ghế · ${formatRideDateTime(departureAt)}` : disabledReason}</small><button type="button" className="customer-ride-primary full" disabled={!canSubmit} onClick={submit}>{submitting ? <><RideAppIcon name="time" className="ride-icon-spin" /> Đang gửi...</> : 'Gửi yêu cầu chuyến đi'}</button></div>
          {optionSheet === 'SCHEDULE' && <RideScheduleSheet initialValue={departureAt} onClose={() => setOptionSheet(null)} onConfirm={(value) => { setDepartureAt(value); setOptionSheet(null); }} />}
          {optionSheet === 'SEATS' && <RideSeatSheet initialSeats={seats} onClose={() => setOptionSheet(null)} onConfirm={(value) => { setSeats(value); setOptionSheet(null); }} />}
        </section>
      </div>
    </RidePortal>
  );
};

export default RideScheduledDemandModal;
